import { Box, Grid, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { useMortgageState } from "../../../contexts/mortgage/Reducer";
import { useHttpClient } from "../../../services/mortgage-client/MortgageClientContext";
import AddressInfo from "../../address-info/AddressInfo";
import ErrorMessage from "../../feedback/ErrorMessage";
import MortgageStepper from "../../mortgage-stepper/MortgageStepper";
import MortgageGeneralProvisions from "../2-summary/MortgageGeneralProvisions";
import NewMortgageSummary from "../2-summary/NewMortgageSummary";
import { SectionDivider, SummarySubHeaderLabel } from "../2-summary/SummaryScreen";

const ThanksBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flex: 1,
    flexFlow: "column nowrap",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "40px",
    [theme.breakpoints.up("lg")]: {
        marginBottom: "32px",
    },
    height: "360px",
    position: "relative",
}));

const OverlayImageThanksBox = (props: { imageUrl: string; children?: React.ReactNode }) => (
    <Box
        sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "360px",
            backgroundImage: `url("${props.imageUrl}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            pointerEvents: "none",
        }}>
        {props.children}
    </Box>
);

const ThanksGrayBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flex: 1,
    flexFlow: "column nowrap",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingBottom: "25px",
    paddingTop: "42px",
    paddingLeft: "16px",
    paddingRight: "16px",
    width: "100%",
    [theme.breakpoints.up("md")]: {
        paddingTop: "50px",
        paddingBottom: "37px",
        marginTop: "32px",
    },
    background: "#FAFAFA",
}));

const ThanksHeaderLabel = styled(Typography)(({ theme }) => ({
    color: "#333333",
    fontWeight: 400,
    fontSize: "32px",
    lineHeight: "39px",
    letterSpacing: 0,
    textAlign: "center",
    marginTop: "12px",
    marginBottom: "16px",
    [theme.breakpoints.up("lg")]: {
        fontSize: "48px",
        lineHeight: "54px",
        fontWeight: "300",
    },
}));

const ThanksMessageLabel = styled(Typography)(({ theme }) => ({
    color: "#333333",
    fontSize: "18px",
    fontWeight: "300",
    letterSpacing: 0,
    lineHeight: "26px",
    textAlign: "center",
    marginBottom: "16px",
    [theme.breakpoints.up("lg")]: {
        marginBottom: "24px",
    },
}));

/**
 * A third and the last screen that confirms to the customer that the process was successful.
 */
const ConfirmationScreen = () => {
    const { t } = useTranslation();

    const httpClient = useHttpClient();
    const { mortgage } = useMortgageState();

    const theme = useTheme();
    const matchesWebView = useMediaQuery(theme.breakpoints.up("lg"));

    if (mortgage === undefined) {
        return <ErrorMessage />;
    }

    return (
        <Grid container sx={{ marginBottom: "66px" }} data-cy={"confirmation-screen"}>
            <Grid item sm={12} md={12} lg={12}>
                <MortgageStepper />
            </Grid>

            <Grid item sm={12} md={12} lg={12}>
                <ThanksBox>
                    <OverlayImageThanksBox
                        imageUrl={httpClient.buildUrl(
                            matchesWebView ? "icons/congrats-image.svg" : "icons/congrats-image-mobile.svg"
                        )}
                    />
                    <Grid container>
                        <Grid item sm={0} md={1} lg={1}></Grid>
                        <Grid item sm={12} md={10} lg={10}>
                            <ThanksGrayBox>
                                <Box>
                                    <ThanksHeaderLabel>
                                        {t("ConfirmationScreen.Thank you for placing your trust in us.")}
                                    </ThanksHeaderLabel>
                                </Box>
                                <Box>
                                    <ThanksMessageLabel>
                                        {t(
                                            "ConfirmationScreen.You will be sent the contractual documents via online banking or by post in the next few days."
                                        )}
                                    </ThanksMessageLabel>
                                </Box>
                            </ThanksGrayBox>
                        </Grid>
                        <Grid item sm={0} md={1} lg={1}></Grid>
                    </Grid>
                </ThanksBox>
            </Grid>

            <Grid item sm={0} md={1} lg={1}></Grid>
            <Grid item sm={12} md={10} lg={10}>
                <AddressInfo selectedMortgage={mortgage} />
                <Box display={"flex"} alignItems="center" justifyContent="flex-start">
                    <SummarySubHeaderLabel>{t("SummaryScreen.Your new mortgage")}</SummarySubHeaderLabel>
                </Box>
                <NewMortgageSummary />
                <SectionDivider />
                <Box display={"flex"} alignItems="center" justifyContent="flex-start">
                    <SummarySubHeaderLabel>{t("General.General terms")}</SummarySubHeaderLabel>
                </Box>
                <MortgageGeneralProvisions />
            </Grid>
            <Grid item sm={0} md={1} lg={1}></Grid>
        </Grid>
    );
};
export default ConfirmationScreen;
